/**
 * Category Chips — horizontal scrollable category filter.
 * Used in All tab and Experts tab for filtering challenges by category.
 */

import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { useChallengeCategories } from '@turnifly/shared/hooks'
import { Skeleton } from '@turnifly/shared/ui'

interface CategoryChipsProps {
  selected: string | null
  onSelect: (categoryId: string | null) => void
}

export const CategoryChips = memo(function CategoryChips({
  selected,
  onSelect,
}: CategoryChipsProps): React.ReactElement {
  const { t, i18n } = useTranslation()
  const { data: categories, isLoading } = useChallengeCategories()
  const lang = i18n.language as 'uz' | 'en'

  if (isLoading) {
    return (
      <div className="flex gap-2 overflow-hidden pb-1">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-20 shrink-0 rounded-full" />
        ))}
      </div>
    )
  }

  return (
    <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1 -mx-1 px-1">
      {/* "All" chip */}
      <button
        onClick={() => onSelect(null)}
        className={`shrink-0 flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12px] font-medium transition-colors tap-highlight-transparent ${
          selected === null
            ? 'bg-primary text-white'
            : 'bg-white/[0.04] text-text-hint border border-white/[0.06] hover:bg-white/[0.06]'
        }`}
      >
        🔥 {t('challenges.browse.allCategories')}
      </button>

      {(categories ?? []).map((cat) => (
        <button
          key={cat.id}
          onClick={() => onSelect(cat.id)}
          className={`shrink-0 flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12px] font-medium transition-colors tap-highlight-transparent ${
            selected === cat.id
              ? 'bg-primary text-white'
              : 'bg-white/[0.04] text-text-hint border border-white/[0.06] hover:bg-white/[0.06]'
          }`}
        >
          {cat.icon} {lang === 'uz' ? cat.name_uz : cat.name_en}
        </button>
      ))}
    </div>
  )
})
